import React from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Menu,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import {
  FilterButton,
  StyledSegmentedControl,
  StyledSegmentButton,
  StyledActionButton,
} from './SectionBar.styled';

interface ItemsKgReportFiltersProps {
  customFilters: {
    fromPeriod?: string;
    toPeriod?: string;
    direction?: string;
    compactOnly?: boolean;
    onFromPeriodChange?: (period: string) => void;
    onToPeriodChange?: (period: string) => void;
    onDirectionChange?: (direction: string) => void;
    onCompactOnlyChange?: (value: boolean) => void;
    onExport?: (format: 'HTML' | 'XLS' | 'PDF') => void;
    isExporting?: boolean;
    yearQuarterOptions?: Array<{ value: string; label: string; year: string; quarter: string }>;
  };
}

const ItemsKgReportFilters: React.FC<ItemsKgReportFiltersProps> = ({ customFilters }) => {
  // Generate year-quarter options from 2008 to current year
  // Use cascaded options from parent if provided, otherwise generate all options
  const yearQuarterOptions = React.useMemo(() => {
    // If parent provides cascaded options, use them
    if (customFilters.yearQuarterOptions && customFilters.yearQuarterOptions.length > 0) {
      return customFilters.yearQuarterOptions;
    }
    
    // Otherwise, generate all options
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= 2008; year--) { // Reverse order to show recent years first
      quarters.forEach(quarter => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({
          value,
          label,
          year: year.toString(),
          quarter
        });
      });
    }
    
    return options;
  }, [customFilters.yearQuarterOptions]);

  // Direction filter options
  const directionOptions = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  // For period dropdown menu handling
  const [fromPeriodAnchorEl, setFromPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const [toPeriodAnchorEl, setToPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const isFromPeriodMenuOpen = Boolean(fromPeriodAnchorEl);
  const isToPeriodMenuOpen = Boolean(toPeriodAnchorEl);

  // Period handlers
  const handleFromPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setFromPeriodAnchorEl(event.currentTarget);
  };

  const handleFromPeriodMenuClose = () => {
    setFromPeriodAnchorEl(null);
  };

  const handleFromPeriodChange = (period: string) => {
    if (customFilters.onFromPeriodChange) {
      customFilters.onFromPeriodChange(period);
    }
    handleFromPeriodMenuClose();
  };

  const handleToPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setToPeriodAnchorEl(event.currentTarget);
  };

  const handleToPeriodMenuClose = () => {
    setToPeriodAnchorEl(null);
  };

  const handleToPeriodChange = (period: string) => {
    if (customFilters.onToPeriodChange) {
      customFilters.onToPeriodChange(period);
    }
    handleToPeriodMenuClose();
  };

  // Get the displayed period labels
  const getFromPeriodLabel = () => {
    if (!customFilters.fromPeriod) return 'Select From';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.fromPeriod);
    return option ? option.label : 'Select From';
  };

  const getToPeriodLabel = () => {
    if (!customFilters.toPeriod) return 'Select To';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.toPeriod);
    return option ? option.label : 'Select To';
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      {/* From Period Filter */}
      <FilterButton onClick={handleFromPeriodClick}>
        <span className='label'>From</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getFromPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      {/* From Period Dropdown Menu */}
      <Menu
        anchorEl={fromPeriodAnchorEl}
        open={isFromPeriodMenuOpen}
        onClose={handleFromPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleFromPeriodChange('')}>
          <Typography sx={{ fontSize: '14px' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleFromPeriodChange(option.value)}
            selected={customFilters.fromPeriod === option.value}
            sx={{
              fontWeight: customFilters.fromPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* To Period Filter */}
      <FilterButton onClick={handleToPeriodClick}>
        <span className='label'>To</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getToPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      {/* To Period Dropdown Menu */}
      <Menu
        anchorEl={toPeriodAnchorEl}
        open={isToPeriodMenuOpen}
        onClose={handleToPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleToPeriodChange('')}>
          <Typography sx={{ fontSize: '14px' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleToPeriodChange(option.value)}
            selected={customFilters.toPeriod === option.value}
            sx={{
              fontWeight: customFilters.toPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Direction Filter using Segmented Control */}
      <StyledSegmentedControl>
        {directionOptions.map((option) => (
          <StyledSegmentButton
            key={option.id}
            active={customFilters.direction === option.id}
            onClick={() => customFilters.onDirectionChange?.(option.id)}
            sx={{
              color: customFilters.direction === option.id ? '#ffffff' : '#00B6BA',
              minWidth: '70px',
            }}
          >
            {option.label}
          </StyledSegmentButton>
        ))}
      </StyledSegmentedControl>

      {/* Compact Only Checkbox */}
      <Box sx={{ ml: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={customFilters.compactOnly || false}
              onChange={(e) => customFilters.onCompactOnlyChange?.(e.target.checked)}
              sx={{
                color: '#00B6BA',
                '&.Mui-checked': {
                  color: '#00B6BA',
                },
                '& .MuiSvgIcon-root': {
                  fontSize: 18,
                },
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#063142' }}>
              UNEX Only
            </Typography>
          }
        />
      </Box>

      {/* Export Buttons */}
      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('HTML')}
          disabled={customFilters.isExporting}
          sx={{ minWidth: '60px' }}
        >
          {customFilters.isExporting ? (
            <CircularProgress size={16} />
          ) : (
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>HTML</Typography>
          )}
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('XLS')}
          disabled={customFilters.isExporting}
          sx={{ minWidth: '60px' }}
        >
          {customFilters.isExporting ? (
            <CircularProgress size={16} />
          ) : (
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>XLS</Typography>
          )}
        </StyledActionButton>
      </Box>
    </Box>
  );
};

export default ItemsKgReportFilters; 